import type { IFSItem } from 'bz-core-controls/hooks-form/controls/field-select';
import createContext from 'bz-utilities/hooks/context-v2';
import CacheStore from 'bz-utilities/modules/stores/cached-map-store';
import GUID from 'bz-utilities/modules/variables/guid';
import { computed } from 'mobx';
import type { ITransformer } from 'mobx-utils';
import { createTransformer } from 'mobx-utils';
import type { StockResponse, StockWithLocationResponse } from 'tm3-core/api.dtos';
import StockCategory from 'tm3-core/interfaces/stock/enums/category';
import { cache } from '../index';

type IStockKeys =
  | 'id'
  | 'reference'
  | 'category'
  | 'appointmentLength'
  | 'isMultiple'
  | 'isPrivateCharge'
  | 'serviceTypeId'
  | 'onlineGuid'
  | 'isVideo';

export const [InventoryStoreCtx, useInventoryStore] =
  createContext<InventoryStore>('InventoryStore');

export interface IChargeControlStock extends Pick<StockResponse, IStockKeys> {
  isPrePaid: boolean;
  name: string;
}

class InventoryStore extends CacheStore<StockWithLocationResponse> {
  constructor() {
    super('inventory');
  }
  @computed
  get Current() {
    return this.All.filter((x) => x.inUse);
  }

  @computed
  get Online() {
    return this.All.filter((x) => x.inUse && x.online && !x.locationId);
  }

  @computed
  get AllOnline() {
    return this.All.filter((x) => x.online && !x.locationId);
  }

  public search(
    searchTerm: string = '',
    privateOnlyCharges = false,
    allowedCatergories: StockCategory[] = []
  ): StockWithLocationResponse[] {
    if (!searchTerm.trim()) return []; // Return early if searchTerm is empty or whitespace

    const lowerSearchTerm = searchTerm.toLowerCase();

    return this.Current.filter((stock) => {
      const matchesCategory =
        !allowedCatergories.length || allowedCatergories.includes(stock.category as StockCategory);
      const matchesPrivateCharge = !privateOnlyCharges || !!stock.isPrivateCharge; // Check private charges only if flag is true
      const matchesDescription = (stock.description ?? '').toLowerCase().includes(lowerSearchTerm);

      return matchesCategory && matchesPrivateCharge && matchesDescription; // Combine conditions
    });
  }

  ByLocationWithPrice = createTransformer((locationId: number) => {
    return this.Current.filter((x) => !x.locationId || x.locationId === locationId).map((x) =>
      this.dropDownFormat(x, true, true, locationId)
    );
  });

  ByLocation = createTransformer((locationId: number) => {
    return this.Current.filter((x) => !x.locationId || x.locationId === locationId).map((x) =>
      this.dropDownFormat(x, false, true, locationId)
    );
  });

  ForAppointmentSelection: ITransformer<AppointmentStockSelectionParams, IChargeControlStock[]> =
    createTransformer((args: AppointmentStockSelectionParams) => {
      const { FILTERMODE = 'OFF' } = cache.config;

      const practitioner = args.practitionerId
        ? cache.practitioners.getById(args.practitionerId)
        : undefined;

      return this.All.filter((stock: StockWithLocationResponse) => {
        const {
          locationId,
          serviceTypeId,
          overrides,
          types,
          excludeMulti,
          skipFilterMode,
          groupId,
          sessionSkillRestrictions
        } = args;

        // if it's an override it should be included regardless of filtering.
        if (overrides.includes(stock.id)) {
          return true;
        }

        // exclude deactivated stock
        if (!stock.inUse) {
          return false;
        }

        // check stock is of the correct service type id
        if (serviceTypeId > 0 && stock.serviceTypeId !== serviceTypeId) {
          return false;
        }

        // check category filter condition
        if (types.length > 0 && !types.includes(stock.category as 'B' | 'C' | 'S' | 'P')) {
          return false;
        }

        // exclude any multi stock if required
        if (excludeMulti && stock.isMultiple) {
          return false;
        }

        // if using strict or flex filter modes need to check group vs private stock
        if (FILTERMODE !== 'OFF' && !skipFilterMode) {
          if (groupId > 0 ? !stock.groups.includes(groupId) : !stock.isPrivateCharge) {
            return false;
          }
        }

        // if it has a skill restriction on the session
        if (
          !skipFilterMode &&
          sessionSkillRestrictions?.length > 0 &&
          (!stock.specialismId || !sessionSkillRestrictions.includes(stock.specialismId))
        )
          return false;

        // if it has a skill restriction on the practitioners
        if (
          !skipFilterMode &&
          stock.specialismId &&
          !GUID.isEmpty(stock.specialismId) &&
          (!practitioner || !practitioner.skills.includes(stock.specialismId))
        )
          return false;

        if (!!cache.isClick) {
          // if click no need to check online status
          return !stock.locationId || stock.locationId === locationId;
        }

        // if it's online it'll have no location id, otherwise, compare on location id
        return (!stock.locationId && stock.online) || stock.locationId === locationId;
      }).map((x) => this.dropDownFormat(x, args.showPrice, true, args.locationId));
    });

  dropDownPrepaidFormat(stock: StockWithLocationResponse): IChargeControlStock {
    let name = stock.description;
    if (stock.appointmentLength != null) {
      name = name + ' - ' + stock.appointmentLength + ' minute(s)';
    }
    if (stock.reference != null) {
      name = stock.reference + ' - ' + name;
    }
    name += ' [PrePaid]';
    return {
      id: stock.id,
      name,
      reference: stock.reference || '',
      category: stock.category,
      appointmentLength: stock.appointmentLength,
      isMultiple: stock.isMultiple,
      isPrivateCharge: stock.isPrivateCharge,
      isPrePaid: true,
      serviceTypeId: stock.serviceTypeId,
      isVideo: stock.isVideo
    };
  }

  @computed
  get CurrentFormattedForDropdown(): IFSItem[] {
    return this.Current.map((stock) => {
      return {
        text: this.getDescription(stock),
        payload: stock.id
      };
    });
  }

  dropDownFormat(
    stock: StockWithLocationResponse,
    showPrice: boolean = false,
    showDuration: boolean = false,
    locationId?: number
  ): IChargeControlStock {
    let name = this.getDescription(stock, showPrice, showDuration, locationId);
    return {
      id: stock.id,
      name,
      reference: stock.reference || '',
      category: stock.category,
      appointmentLength: stock.appointmentLength,
      isMultiple: stock.isMultiple,
      isPrivateCharge: stock.isPrivateCharge,
      isPrePaid: false,
      serviceTypeId: stock.serviceTypeId,
      isVideo: stock.isVideo,
      onlineGuid: stock.onlineGuid
    };
  }

  public getDescription(
    stock: StockWithLocationResponse,
    showPrice: boolean = false,
    showDuration: boolean = false,
    locationId?: number
  ) {
    let name;
    if (showPrice) {
      let price = stock.totalPrice;
      if (locationId) {
        const stockOverride = stock.overrides.find((x) => x.locationId === locationId);
        if (stockOverride) {
          price = stockOverride.totalPrice;
        }
      }
      const {
        config: { CURRENCYSYMBOL = '£' }
      } = cache;
      name = stock.description + ' - ' + CURRENCYSYMBOL + ' ' + (price ?? 0).toFixed(2);
    } else {
      name = stock.description;
    }
    if (
      stock.category === StockCategory.consultancy &&
      stock.appointmentLength != null &&
      showDuration
    ) {
      name = name + ' - ' + stock.appointmentLength + ' minute(s)';
    }
    if (stock.reference != null) {
      name = stock.reference + ' - ' + name;
    }
    if (stock.category === StockCategory.stock) {
      if (
        stock.level === 0 ||
        (locationId &&
          stock.overrides.some((o) => o.locationId === stock.locationId && o.stockLevel === 0))
      ) {
        name += ' [OUT OF STOCK]';
      }
    }
    return name;
  }

  public readonly getByGuid = createTransformer<string, StockWithLocationResponse | null>((id) => {
    if (!id) {
      return null;
    }
    return this.All.find((item) => item.onlineGuid === id) || null;
  });
}

export class AppointmentStockSelectionParams {
  locationId: number = -1;
  groupId: number = -1;
  practitionerId: number = -1;
  serviceTypeId: number = 1;
  types: ('B' | 'C' | 'S' | 'P')[] = ['C', 'S'];
  excludeMulti: boolean = false;
  skipFilterMode: boolean = false;
  overrides: number[] = [];
  showPrice: boolean = false;
  sessionSkillRestrictions: string[] = [];

  public constructor(init?: Partial<AppointmentStockSelectionParams>) {
    // c# object initialiser  style nearly....
    (Object as any).assign(this, init);
  }
}

export default InventoryStore;
